import type { ShopifyRuleFilterScope } from "@loot-os/api-contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useTRPC } from "./trpc";

/**
 * Asks the merchant for an optional scope a rule filter reads (`read_locations`,
 * `read_metaobjects`), in Shopify's own grant modal. On a grant the server stores the scopes and
 * syncs what they unlock (`catalog.syncScopes`), and the builder's choices are read again.
 */
export function useRuleFilterScopes(): {
  request: (scope: ShopifyRuleFilterScope) => Promise<void>;
  pending: ShopifyRuleFilterScope | null;
} {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const syncScopes = useMutation(trpc.catalog.syncScopes.mutationOptions());
  const [pending, setPending] = useState<ShopifyRuleFilterScope | null>(null);

  async function request(scope: ShopifyRuleFilterScope) {
    setPending(scope);
    try {
      const response = await shopify.scopes.request([scope]);
      if (response.result !== "granted-all") {
        shopify.toast.show("Access not granted. You can allow it any time from here.");
        return;
      }
      const synced = await syncScopes.mutateAsync({ scope });
      await queryClient.invalidateQueries({ queryKey: trpc.catalog.facets.queryKey() });
      if (!synced.complete) {
        shopify.toast.show(
          "Still reading your products. Their attributes appear in a few minutes.",
        );
      }
    } catch {
      shopify.toast.show("Access could not be saved. Try again.", { isError: true });
    } finally {
      setPending(null);
    }
  }

  return { request, pending };
}
